"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { onDeparture } from "@/lib/analytics/departure";
import type { StudioEmptyOrigin, StudioEntryDoor } from "@/lib/analytics";
import { createStudioEntryFunnel } from "@/lib/analytics/studio-entry";

export type UseStudioEntryFunnelOptions = {
  /** La carte de bienvenue est sous les yeux — la route en fait partie. */
  readonly welcomeVisible: boolean;
  /**
   * Le studio vide *est* l'écran : bonne route, bonne vue, mode libre,
   * démarrage terminé, aucun morceau chargé.
   */
  readonly emptyMounted: boolean;
  /**
   * L'invitation elle-même est affichée par-dessus le roll.
   *
   * Séparée de `emptyMounted` parce que les deux ne disparaissent pas pour la
   * même raison, et que la différence est justement ce qu'on cherche à
   * mesurer : ranger l'invitation en restant sur un roll vide, c'est jouer ;
   * quitter l'écran, c'est partir.
   *
   * Le cadre de dépôt et le message d'erreur de fichier n'entrent pas dans ce
   * booléen bien qu'ils masquent l'invitation : un fichier traîné au-dessus du
   * roll est un import qui commence, pas un départ, et le compter comme tel
   * fabriquerait un renvoi suivi d'une exposition neuve à chaque survol.
   */
  readonly emptyOffered: boolean;
  readonly authenticated: boolean;
  /**
   * Une reprise est-elle proposée en ce moment ? Lu au moment de l'exposition,
   * jamais pendant un rendu, d'où le rappel plutôt que la valeur.
   *
   * Doit être stable d'un rendu à l'autre : il entre dans les dépendances de
   * l'effet qui constate l'exposition.
   */
  readonly hasResumeOffer: () => boolean;
};

export type StudioEntryReporter = {
  readonly entered: () => void;
  readonly doorChosen: (door: StudioEntryDoor) => void;
  /** Le bouton « Aide » a rappelé l'écran vide. */
  readonly recalled: () => void;
};

/**
 * Branche l'entonnoir d'entrée du studio sur ce que la page voit.
 *
 * Le hook ne décide de rien : il observe deux transitions (la carte apparaît,
 * l'écran vide apparaît ou disparaît) et transmet les gestes. Toute la règle
 * de non-répétition vit dans `lib/analytics/studio-entry`, qui se teste sans
 * React.
 */
export function useStudioEntryFunnel({
  welcomeVisible,
  emptyMounted,
  emptyOffered,
  authenticated,
  hasResumeOffer,
}: UseStudioEntryFunnelOptions): StudioEntryReporter {
  const [funnel] = useState(createStudioEntryFunnel);
  /**
   * Une exposition n'est neuve qu'une fois. Ensuite l'écran revient de
   * lui-même après une pause, ou parce qu'on a cliqué « Aide » — et confondre
   * les trois gonflerait avec des retours le dénominateur qui doit compter des
   * arrivées.
   */
  const originRef = useRef<StudioEmptyOrigin>("arrival");

  const exposed = emptyMounted && emptyOffered;

  useEffect(() => {
    if (!welcomeVisible) {
      return;
    }

    funnel.welcomeShown(authenticated);
  }, [funnel, welcomeVisible, authenticated]);

  useEffect(() => {
    if (!exposed) {
      funnel.emptyHidden(emptyMounted ? "played" : "left");

      return;
    }

    funnel.emptyShown({
      origin: originRef.current,
      hasResumeOffer: hasResumeOffer(),
      authenticated,
    });
    originRef.current = "reappear";

    /*
      Le départ **et** le retour, et pas seulement `pagehide`.

      `pagehide` n'arrive pas quand l'application passe en arrière-plan puis est
      tuée par l'OS, ce qui est le sort ordinaire d'un onglet sur la tablette du
      pupitre. Or c'est ici que se mesure le silence : quelqu'un resté planté
      devant l'écran vide et quelqu'un qui n'est jamais venu se ressemblent trait
      pour trait tant que rien ne les distingue. Sans le second signal, ce
      chiffre manquait systématiquement sur l'appareil que le produit vise.

      La réouverture n'est pas un ornement : sans elle, revenir au premier plan
      laisserait un écran vide bien réel hors du dénominateur, et la porte prise
      ensuite n'aurait plus d'exposition à laquelle se rattacher. Elle passe par
      `emptyShown` avec l'origine courante, qui vaut déjà « reappear » à ce
      stade — c'est exactement le vocabulaire prévu pour un écran qui revient.
      `onDeparture` vide la file lui-même, sans quoi l'onglet fermé devant
      l'écran vide serait mis en file après le vidage du client, et perdu.
    */
    const stopWatchingDeparture = onDeparture({
      close: () => {
        funnel.emptyHidden("pagehide");
      },
      reopen: () => {
        funnel.emptyShown({
          origin: originRef.current,
          hasResumeOffer: hasResumeOffer(),
          authenticated,
        });
      },
    });

    // Le nettoyage ne clôt rien : il tourne à chaque changement de dépendance,
    // et une fermeture émise ici doublerait celle du passage à `!exposed`.
    return stopWatchingDeparture;
  }, [funnel, exposed, emptyMounted, authenticated, hasResumeOffer]);

  const entered = useCallback(() => {
    funnel.entered();
  }, [funnel]);

  const doorChosen = useCallback(
    (door: StudioEntryDoor) => {
      funnel.doorChosen(door);
    },
    [funnel],
  );

  const recalled = useCallback(() => {
    originRef.current = "recall";
  }, []);

  return useMemo(
    () => ({ entered, doorChosen, recalled }),
    [entered, doorChosen, recalled],
  );
}
